import axios from 'axios';
import { ACTIVITY_ACTION, ACTIVITY_TYPE, PROJECT_STATUS, PROJECT_STATUS_TEXT } from '../global/constants';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import {
  ProjectDetailInterface,
  ProjectFilesInterface,
  AddMessageReactionInterface,
  AddNewFilePayloadInterface,
  CreateMilestonePayloadInterface,
  CreateNewDocInterface,
  DocDataInterface,
  MilestoneListInterface,
  MilestoneTaskInterface,
  UpdateFileDataPayload,
  ProjectResourceInterface,
  StatusInputInterface,
  StatusListInterface,
  UpdateProjectMemberInterface,
  CreateProjectPayloadInterface,
  CreateProjectPayloadInterfaceV2
} from '../interfaces/ProjectInterface';
import { getAPIErrorReason, isDev, isEmpty } from '../helpers/common';
import FilesCacheService from './filesCacheService';
import { trackAnalyticActivity } from './analyticsService';
import dayjs from 'dayjs';
import { loadProjectDiscussion, refreshProjectDiscussion } from './conversationSyncService';
import { captureException } from './logService';
import { UsersWorkspaceInterface } from '../interfaces/WorkspaceInterface';
import { PROJECT_ANALYTICS } from '../global/analyticsConstants';
import {
  ApiLabel,
  FileListItem,
  Label,
  Milestone,
  ProjectApiResponse,
  ProjectProgress,
  ProjectTask,
  ProjectUpdatePayloadInterface,
  RawMilestone,
  RawProject,
  RawProjectFile,
  SharePayload
} from '../mobx/interfaces/project';
import { rootStore } from '../mobx/rootStore';
import { UserModel } from '../mobx/models/user';
import { defaultDisplayFilters, mergeTaskFilter } from '../mobx/common';
import { getWorkspaceProjectPreferenceData } from '../helpers/firebaseHelper';
import { User } from '@sentry/react';
import { ApiActivityItem } from '../mobx/interfaces/user';
import { NewTaskFilterInterface } from '../interfaces/TaskInterface';

export const getDefaultProgress = (): ProjectProgress => {
  const status = PROJECT_STATUS.NOT_STARTED;
  return {
    progress: 0,
    completedTasks: 0,
    totalTasks: 0,
    status,
    statusText: PROJECT_STATUS_TEXT[status]
  };
};

/**
 * @desc Project: Determines the status of a project based on its tasks.
 */
export const determineProjectStatus = (projectTask: ProjectTask[]) => {
  const completedTasks = projectTask?.filter((x: { status: boolean }) => x?.status)?.length || 0;
  const openTasks = projectTask?.filter((x: { status: boolean }) => !x?.status)?.length || 0;

  // If there are no open tasks
  if (openTasks === 0) {
    if (completedTasks === 0) {
      return PROJECT_STATUS.NOT_STARTED;
    }
    return PROJECT_STATUS.ON_SCHEDULE;
  }

  // If there are open tasks
  const overdueTasks =
    projectTask?.filter(
      (x) =>
        !x?.status &&
        x?.dueDate !== null &&
        x?.dueDate !== undefined &&
        new Date(x.dueDate).getTime() < new Date().getTime()
    )?.length || 0;

  const overduePercentage = (overdueTasks / openTasks) * 100;

  if (overduePercentage > 50) {
    return PROJECT_STATUS.BEHIND_SCHEDULE;
  }

  if (overduePercentage > 20) {
    return PROJECT_STATUS.AT_RISK;
  }

  return PROJECT_STATUS.ON_SCHEDULE;
};

/**
 * @desc Project - create new project
 * @param {*}
 */
export const createNewProject = async (payload: CreateProjectPayloadInterface | CreateProjectPayloadInterfaceV2) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/project/postProject`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create project please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - create new label
 * @param project_Id -  The ID of the project
 */
export const createNewLabel = async (payload: { name: string; color: string }, project_Id: string | undefined) => {
  try {
    if (!project_Id) return undefined;
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/project/addProjectLabels/${project_Id}`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create label please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  }
};

/**
 * @desc Project - update label
 * @param project_id -  The ID of the project
 * @param label_id - The ID of the label to be updated
 */
export const updateLabel = async (
  payload: { name: string; color: string },
  project_id: string | undefined,
  label_id: string | undefined
) => {
  try {
    if (!project_id) return false;
    const params = { labelId: label_id };
    const response = await axios.put(
      `${REACT_APP_TEAMCAMP_APIURL}/project/updateRemoveProjectLabels/${project_id}`,
      payload,
      { params }
    );
    if (response) {
      dispatchProjectSuccess('label Updated Successfully');
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update label please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  }
};

/**
 * @desc Project - delete invited member
 * @param label_id - The ID of the label to be deleted
 * @param project_id - The ID of the project
 */
export const deletelabel = async (label_id: string, project_id: string | undefined) => {
  try {
    if (!project_id) return false;
    const params = {
      labelId: label_id
    };
    const response = await axios.delete(
      `${REACT_APP_TEAMCAMP_APIURL}/project/updateRemoveProjectLabels/${project_id}`,
      {
        params
      }
    );
    if (response) {
      dispatchProjectSuccess('Label deleted sucessfully.');
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete label please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - Favorite/UnFavorite Project
 * @param {string} project_id - The ID of the project
 * @param {boolean} value - The value to set for the favorite status
 */
export const favoriteProject = async (project_id: string, value: boolean) => {
  try {
    rootStore.projectStore.setLoading(true);
    const payload = {
      project_id: project_id,
      value: value
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/project/putFavorite`, payload);
    const { data } = response;
    if (data) {
      if (value) trackAnalyticActivity(PROJECT_ANALYTICS.MARKED_FAVORITE);
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to favorite project please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

//Project - get project files
const getRawProjectFile = (item: ProjectFilesInterface): RawProjectFile => {
  const user = rootStore?.workspaceStore?.currentWorkspace?.users.find((a: UserModel) => a?.id === item?.createdBy);
  return {
    createdBy: item?.createdBy,
    createdOn: item?.createdOn,
    fileType: item?.fileType || '',
    fileExt: item?.fileExt,
    isArchived: item?.isArchived,
    name: item?.name,
    parentId: item?.parentId || null,
    projectId: item?.projectId,
    size: Number(item?.size),
    uploadFileType: item?.uploadFileType,
    url: item?.url,
    id: item?.id,
    user
  };
};

const fetchLocalFiles = async (project_id: string) => {
  const local_data = await FilesCacheService.getInstance()?.getFilesByTable(project_id);
  return local_data?.[0]?.value;
};

/**
 * @desc Project - get project files
 * @param isMobx - boolean to determine if using MobX
 */
export const getProjectFiles = async (
  project_id: string | undefined,
  _users?: UsersWorkspaceInterface[] | UserModel[],
  isMobx?: boolean
) => {
  try {
    rootStore.projectStore.setLoading(true);
    if (!project_id) return undefined;

    const workspace = rootStore.workspaceStore.currentWorkspace;
    const workspace_id = workspace?.id;
    if (!workspace_id) return undefined;
    const users = workspace?.users || [];

    const result = await fetchLocalFiles(project_id);
    if (result && result?.length > 0) {
      const rawProjectFiles = result?.map(getRawProjectFile);
      rootStore.projectStore.currentProject?.setProjectFiles(rawProjectFiles);
    } else {
      rootStore.projectStore.currentProject?.setProjectFiles([]);
    }

    const lastUpdatedTime = await FilesCacheService.getInstance()?.getLastUpdatedTime(project_id);

    const params = { lastUpdatedTime };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/files/getFilesList/${project_id}`, { params });

    const { data } = response;
    if (data?.length > 0) {
      const updatedData = data?.map((item: FileListItem) => {
        let user = users?.find((x: UserModel) => x.id === item?.CreatedBy);
        const extension = (name: string) => {
          let fileExt;
          const ext = name?.split('.');
          if (ext && ext.length >= 2) {
            fileExt = '.' + ext[1];
          }
          return fileExt;
        };
        return {
          createdBy: item?.CreatedBy,
          createdOn: item?.CreatedTime,
          fileType: item?.FileType,
          fileExt: extension(item?.Name),
          isArchived: item?.IsArchived,
          name: item?.Name,
          parentId: item?.ParentId,
          projectId: item?.ProjectId,
          size: Number(item?.Size),
          uploadFileType: item?.UploadFileType,
          url: item?.Url,
          id: item['_id'],
          user: user?.id
        };
      });
      const activeFiles = updatedData.filter((item: FileListItem) => !item.isArchived);
      const archivedFiles = updatedData.filter((item: FileListItem) => item.isArchived);

      const adjustedTime = dayjs().subtract(1, 'hour').toISOString();

      let hasUpdatedData = false;
      if (lastUpdatedTime === 0 || isEmpty(lastUpdatedTime)) {
        await FilesCacheService.getInstance()?.setFiles(project_id, activeFiles, workspace_id);
        hasUpdatedData = true;
      } else {
        await FilesCacheService.getInstance()?.updateProjectFiles(activeFiles, archivedFiles, project_id);
        hasUpdatedData = !!(activeFiles.length > 0 || archivedFiles.length > 0);
      }
      await FilesCacheService.getInstance()?.setLastUpdatedTime(adjustedTime, workspace_id, project_id);

      if (hasUpdatedData) {
        const updatedLocalData = await fetchLocalFiles(project_id);
        if (updatedLocalData) {
          const rawProjectFiles = updatedLocalData?.map((item) => getRawProjectFile(item));
          rootStore.projectStore.projects?.find((x) => x?.id === project_id)?.setProjectFiles(rawProjectFiles);
          return updatedLocalData;
        }
      }
    } else {
      const selectedProject = rootStore.projectStore.projects?.find((x) => x?.id === project_id);
      const rawProjectFiles = result?.map(getRawProjectFile);
      if (selectedProject) {
        selectedProject?.setProjectFiles(rawProjectFiles || []);
      }
      return rawProjectFiles;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get project files please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

const formatCurrentProjectData = (
  data: ProjectApiResponse,
  updatedLabels: Label[],
  listItemsDisplayFilter?: { [key: number]: boolean }
): RawProject => {
  return {
    companyId: data.CompanyId,
    createdBy: data.CreatedBy,
    createdOn: data.CreatedTime,
    customerId: data.CustomerId && !isEmpty(data.CustomerId) ? data.CustomerId : '',
    description: data.Description,
    dueDate: data.DueDate,
    id: data['_id'],
    identifier: data.Identifier || undefined,
    isEstimate: data.IsEstimate,
    isFavorite: data.IsFavorite,
    name: data.Name,
    startDate: data.StartDate,
    updatedOn: data.UpdatedTime,
    users: data.ProjectUsers,
    priority: {
      default: data.Priority?.Default ?? 0,
      enabled: data.Priority?.Enabled ?? false
    },
    isMilestone: data.IsMilestone,
    labelsEnabled: data.LabelsEnabled,
    labelsList: updatedLabels,
    statusEnable: data.StatusEnable,
    status: data.Status?.filter((x: StatusListInterface) => x) || [],
    isGroupEnabled: data.IsGroupEnabled !== false,
    multiAsignee: data.MultiAsignee,
    githubRepository: data.GithubRepository ?? null,
    resources: data.Resources || [],
    customer: data.Customer || null,
    icon: data.Icon,
    vercelProject: data.vercelProject ?? null,
    taskFilter: {
      ...defaultDisplayFilters,
      ...(data.taskFilter || {})
    },
    listItemsDisplayFilter: listItemsDisplayFilter || {}
  };
};

/**
 * @desc Project - getCurrentProjectDetails
 * @param {string} projectId - The ID of the project
 */
export const getCurrentProjectDetails = async (projectId: string | undefined) => {
  try {
    if (!projectId) return undefined;
    rootStore.projectStore.setLoading(true);
    const workspace = rootStore.workspaceStore.currentWorkspace;
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/v2/project/getCurrentProjectDetails/${projectId}`);
    const { data } = response;
    if (data) {
      const projectUsers = data?.ProjectUsers?.map((user: User) => {
        let userData;
        if (typeof user === 'string') {
          userData = workspace?.users.find((ele) => ele?.id === user);
        } else {
          userData = workspace?.users.find((ele) => ele?.id === user['_id']);
        }
        return userData;
      }).filter(Boolean);
      const updatedLabels = data?.LabelsList?.map((x: ApiLabel) => {
        return {
          id: x['_id'],
          name: x?.Name,
          color: x?.Color,
          createdBy: x?.CreatedBy,
          createdOn: x?.CreatedTime,
          updatedBy: x?.UpdatedBy,
          updatedOn: x?.UpdatedTime
        };
      });
      const defaultStatus = data?.Status?.find((status: StatusListInterface) => status?.Default);
      const projectDetails = {
        companyId: data?.CompanyId,
        createdBy: data?.CreatedBy,
        createdOn: data?.CreatedTime,
        customerId: !isEmpty(data?.CustomerId) ? data?.CustomerId : undefined,
        description: data?.Description,
        dueDate: data?.DueDate,
        id: data['_id'],
        identifier: data?.Identifier,
        isEstimate: data?.IsEstimate,
        isFavorite: data?.IsFavorite,
        name: data?.Name,
        startDate: data?.StartDate,
        updatedOn: data?.UpdatedTime,
        users: projectUsers,
        priority: { default: data?.Priority?.Default, enabled: data?.Priority?.Enabled },
        isMilestone: data?.IsMilestone,
        labelsEnabled: data?.LabelsEnabled,
        labelsList: updatedLabels,
        statusEnable: data?.StatusEnable,
        statusData: data?.Status?.filter((x: StatusListInterface) => x),
        defaultStatus: defaultStatus || data?.Status?.[0] || {},
        isGroupEnabled: data?.IsGroupEnabled === false ? false : true,
        multiAssignee: data?.MultiAsignee,
        githubRepository: data?.GithubRepository,
        resources: data?.Resources || [],
        icon: data?.Icon,
        vercelProject: data?.vercelProject,
        taskFilter: {
          ...defaultDisplayFilters,
          ...(data?.taskFilter || {})
        }
      };

      if (data.CompanyId !== workspace?.id) return undefined;
      // Read the whole preference doc once (not just one field). We use list_items_display_filter
      // here and hand the rest back on the returned object so the caller need not read it again.
      const projectPreference = (await getWorkspaceProjectPreferenceData(projectId)) || undefined;
      const listItemsDisplayFilter = projectPreference?.list_items_display_filter || undefined;
      const projectMobxData = formatCurrentProjectData(data, updatedLabels, listItemsDisplayFilter);
      rootStore.projectStore.upsertProjects([projectMobxData]);
      if (
        !rootStore.projectStore.currentProject?.id ||
        rootStore.projectStore.currentProject?.id === projectDetails.id
      ) {
        rootStore.projectStore.setCurrentProject(projectDetails?.id);
      }
      return { ...projectDetails, projectPreference } as ProjectDetailInterface;
    }
    // If no data is returned from the API, throw to satisfy the return type
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || '`Unable to get Current Project Details please try again`';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - get discussionData
 * @param {string} project_id - The ID of the project
 */
export const getDiscussionData = async (project_id: string) => {
  try {
    const store = rootStore.projectStore;
    // Same discussion already loaded (page re-ran its load, e.g. on a projects update): fetch only changes.
    if (store.activeDiscussionProjectId === project_id && store.discussionUpdatedAt) {
      await refreshProjectDiscussion(project_id);
      return store.discussionData;
    }
    store.setLoading(true);
    return await loadProjectDiscussion(project_id);
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get discussion data please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - update file data
 * @param {string} folder_id - The ID of the folder
 */
export const updateFileData = async (folder_id: string | undefined, payload: UpdateFileDataPayload) => {
  try {
    if (!folder_id) return false;
    rootStore.projectStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/files/updateFileData/${folder_id}`, payload);
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update file data please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - archiveData
 * @param {string} project_id - The ID of the project
 */
export const updateProjectData = async (project_id: string | undefined, payload: ProjectUpdatePayloadInterface) => {
  try {
    if (!project_id) return false;
    rootStore.projectStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/project/putProject/${project_id}`, payload);
    const { data } = response;
    if (data) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update project data please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - create new doc
 * @param {*}
 */
export const createNewDoc = async (payload: CreateNewDocInterface) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/documents/postDocFile`, payload);
    if (response) {
      return response.data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create document please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Member - get User Activity
 * @param {string} userId - The ID of the user
 */
export const getUserActivity = async (userId: string) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/activity/getUserWiseActivityList/${userId}`);
    const { data } = response;
    const currentUser = rootStore.workspaceStore.currentWorkspace?.users?.find((user) => user?.id === userId);
    if (data) {
      const updatedData = data?.map((item: ApiActivityItem) => {
        return {
          actionId: item?.action,
          action: ACTIVITY_ACTION[item?.action],
          createdTime: item?.createdTime,
          desc: item?.referenceName,
          typeId: item?.type,
          type: ACTIVITY_TYPE[item?.type],
          id: item?.id
        };
      });
      currentUser?.setUserActivity(updatedData);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get user activity please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - getProjectArchiveData
 * @param {*}
 */
export const getProjectArchiveData = async () => {
  try {
    rootStore.projectStore.setLoading(true);
    const params = {
      archived: true
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/project/getProjectList`, { params });
    const { data } = response;
    if (data) {
      rootStore.projectStore.setArchiveProjects(data);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Project Archive Data please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - delete invited member
 * @param {string} id - The ID of the user
 * @param {string} project_id - The ID of the project
 * @param {string} invitedUser_id - The ID of the invited user
 * @param {boolean} isAllowAccess - Flag to allow access
 */
export const removeInvitedMember = async (
  id: string,
  project_id: string | undefined,
  invitedUser_id: string,
  isAllowAccess?: boolean,
  isReport = false
) => {
  try {
    rootStore.projectStore.setLoading(true);
    if (!project_id) return false;
    const params = {
      projectId: project_id,
      userId: id,
      Id: invitedUser_id,
      is_allow_access: isAllowAccess || false
    };
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/project/removeTaskUser`, {
      params
    });
    if (response) {
      if (isReport) {
        dispatchProjectSuccess('You leaved the project');
      } else {
        dispatchProjectSuccess(response.data.message);
      }
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to remove invited member please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - add invited member
 * @param {boolean} isAllowAccess - Flag to allow access
 * @param {boolean} isReport - Flag to indicate if it's a report
 */
export const addInvitedMember = async (
  payload: UpdateProjectMemberInterface,
  isAllowAccess?: boolean,
  isReport = false
) => {
  try {
    rootStore.projectStore.setLoading(true);
    const params = {
      is_allow_access: isAllowAccess,
      isDev: isDev()
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/project/InviteUsersInProject`, payload, {
      params
    });
    if (response) {
      if (isReport) {
        dispatchProjectSuccess('You joined successfully');
      } else {
        dispatchProjectSuccess(response.data.message);
      }
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add invited member please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - get doc data
 * @param {string} fileId - The ID of the file
 */
export const getDocumentData = async (fileId: string) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/documents/getDocFile/${fileId}`);
    const { data } = response;
    if (data) {
      const payload = {
        content: data?.Content,
        createdBy: data?.CreatedBy,
        createdOn: data?.CreatedTime,
        name: data?.Name,
        projectId: data?.ProjectId,
        updatedBy: data?.UpdatedBy,
        updatedOn: data?.UpdatedTime,
        id: data['_id'],
        isBlocksuite: data?.IsBlocksuite,
        isPublic: data?.IsPublic,
        isEdge: data?.IsEdge,
        expiryDate: data?.ExpiryTime
      };
      rootStore.projectStore.setDocData(payload);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get doc data please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - get file details by ID
 * @param {string} itemId - The ID of the file
 */
export const getFileDetailsById = async (itemId: string | undefined): Promise<RawProjectFile | null> => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/files/getFileDetails/${itemId}`);
    const { data } = response;
    if (data) {
      const extension = (name: string) => {
        let fileExt;
        const ext = name?.split('.');
        if (ext && ext.length >= 2) {
          fileExt = '.' + ext[1];
        }
        return fileExt;
      };
      let user = rootStore.workspaceStore.currentWorkspace?.users?.find((x: UserModel) => x.id === data?.CreatedBy);
      const fileData: RawProjectFile = {
        createdBy: data?.CreatedBy,
        createdOn: data?.CreatedTime,
        fileType: data?.FileType,
        isArchived: data?.IsArchived,
        name: data?.Name,
        fileExt: extension(data?.Name) || null,
        parentId: data?.ParentId,
        projectId: data?.ProjectId,
        size: data?.Size,
        uploadFileType: data?.UploadFileType,
        url: data?.Url,
        id: data['_id'],
        user
      };
      rootStore.projectStore.setFileViewData(fileData);
      return fileData;
    }
    return null;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get file details please try again';
    captureException(error);
    dispatchProjectError(error);
    return null;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 *  @desc messages - add reaction
 * @param {*}
 */
export const addReactionOnMessage = async (payload: AddMessageReactionInterface) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/discussions/reactionsOnMessage`, payload);
    if (response) {
      return response?.data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add reaction please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 *  @desc Milestone - create New Milestone
 * @param {*}
 */
export const createNewMilestone = async (payload: CreateMilestonePayloadInterface) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/milestone/createMilestone`, payload);
    if (response) {
      dispatchProjectSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create New Milestone please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - get Milestone List
 * @param {string} projectId - The ID of the project
 * @param {boolean} isSelect - Flag to indicate if it's a select list
 * @param {boolean} isForceLoading - Flag to force loading
 */
export const getMilestoneList = async (projectId: string, isSelect = false, isForceLoading = true) => {
  try {
    rootStore.projectStore.setLoading(true);
    const { currentProject } = rootStore.projectStore;
    const milestoneList = currentProject?.milestones;
    if (!isForceLoading && milestoneList && milestoneList?.length > 0 && milestoneList[0]?.projectId === projectId) {
      return milestoneList;
    }
    const params = {
      projectId
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/milestone/getMilestoneList`, {
      params
    });
    const { data } = response;
    if (data) {
      // Mobx set
      const rawMilestones: RawMilestone[] = data?.map((item: MilestoneListInterface) => {
        return {
          id: item?.id,
          milestoneName: item?.milestoneName,
          projectId: item?.projectId,
          createdBy: item?.createdBy,
          createdOn: item?.createdOn,
          index: !item?.index || item?.index === 0 ? new Date(item?.createdOn).getTime() : item?.index,
          isArchived: item?.isArchived ?? false
        };
      });
      rootStore.projectStore.projects.find((project) => project.id === projectId)?.setMilestonesList(rawMilestones);
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get milestone list please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};
/**
 * @desc Project - get Archive Milestone List
 * @param {string} projectId - The ID of the project
 */
export const getArchiveMilestoneList = async (projectId: string) => {
  try {
    rootStore.projectStore.setLoading(true);

    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/milestone/getMilestoneArchiveList/${projectId}`);
    const { data } = response;

    if (data) {
      const rawMilestones: Milestone[] = data?.map((item: MilestoneListInterface) => {
        return {
          id: item?.id,
          milestoneName: item?.milestoneName,
          projectId: item?.projectId,
          createdBy: item?.createdBy,
          createdOn: item?.createdOn,
          index: !item?.index || item?.index === 0 ? new Date(item?.createdOn).getTime() : item?.index,
          isArchived: item?.isArchived ?? false
        };
      });
      rootStore.projectStore.projects
        .find((project) => project.id === projectId)
        ?.setArchiveMilestoneList(rawMilestones);
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get archive milestone list please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - delete Milestone
 * @param {string} milestoneId - The ID of the milestone
 */
export const deleteMilestone = async (milestoneId: string) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/milestone/deleteMilestone/${milestoneId}`);
    if (response) {
      dispatchProjectSuccess(response.data.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete milestone please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - update Milestone
 * @param {boolean} showMessage - Flag to show message
 */
export const updateMilestone = async (payload: MilestoneTaskInterface, showMessage = true) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/milestone/putMilestone`, payload);
    if (response) {
      if (showMessage) dispatchProjectSuccess(response.data.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update milestone please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - add share data
 * @param {*}
 */
export const addShareData = async (payload: SharePayload) => {
  try {
    rootStore.projectStore.setLoading(true);
    const updatedPayload = { ...payload };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/share/postShare`, updatedPayload);
    if (response) {
      return response?.data?.data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add share data please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - get share data
 * @param {string} share_id - The ID of the share
 */
export const getShareData = async (share_id: string) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/share/getShareDetails/${share_id}`);
    if (response) {
      return response?.data?.data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get share data please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - add new file
 * @param {*}
 */
export const addFileData = async (payload: AddNewFilePayloadInterface) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/files/addFileData`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add file please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc update ducument data
 * @param {string} document_id - The ID of the document
 */
export const updateDocumentData = async (document_id: string | undefined, payload: DocDataInterface) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/documents/putDocFile/${document_id}`, payload);
    const { data } = response;
    if (data) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update document data please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - add new status
 * @param {string} projectId - The ID of the project
 */
export const addStatusData = async (projectId: string, payload: StatusInputInterface) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/project/${projectId}/status`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add status please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc update status
 * @param {*}
 */
export const updateStatusData = async (
  projectId: string | undefined,
  statusId: string,
  payload: { name?: string; type?: number; index?: number; default?: boolean }
) => {
  try {
    rootStore.projectStore.setLoading(true);
    if (!projectId) return false;
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/project/${projectId}/status/${statusId}`, payload);
    const { data } = response;
    if (data) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update status data please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - delete Milestone
 * @param {*}
 */
export const deleteStatus = async (projectId: string | undefined, statusId: string | undefined) => {
  try {
    rootStore.projectStore.setLoading(true);
    if (!projectId || !statusId) return false;
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/project/${projectId}/status/${statusId}`);
    if (response) {
      dispatchProjectSuccess(response.data.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete status please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};
/**
 * @desc Project - getProjectListUserNotIn
 * @param {*}
 */
export const getProjectListUserNotIn = async (): Promise<void> => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/project/getProjectsUserNotIn`);
    const { data } = response;
    if (data) {
      rootStore.projectStore.setProjectUserNotIn(data);
      return;
    }
    return;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get project list user not in please try again';
    captureException(error);
    dispatchProjectError(error);
    return;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - add resource
 * @param {string} projectId - The ID of the project
 */
export const addProjectResource = async (projectId: string | undefined, payload: ProjectResourceInterface) => {
  try {
    rootStore.projectStore.setLoading(true);
    if (!projectId) return undefined;
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/project/${projectId}/resource`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add resource please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - update resource
 * @param {string} projectId - The ID of the project
 * @param {string} fileId - The ID of the file
 */
export const updateProjectResource = async (
  projectId: string | undefined,
  fileId: string | undefined,
  payload: { label?: string; reference?: string }
) => {
  try {
    rootStore.projectStore.setLoading(true);
    if (!projectId || !fileId) return undefined;
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/project/${projectId}/resource/${fileId}`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add resource please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - delete resource
 * @param {string} projectId - The ID of the project
 * @param {string} fileId - The ID of the file
 */
export const deleteProjectResource = async (projectId: string | undefined, fileId: string) => {
  try {
    rootStore.projectStore.setLoading(true);
    if (!projectId) return undefined;
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/project/${projectId}/resource/${fileId}`);
    if (response) {
      return true;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add resource please try again';
    captureException(error);
    dispatchProjectError(error);
    return undefined;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - get all raw projects
 * @param {Date} lastUpdatedDate - The date tolast updated
 */
export const getAllRawProjects = async (lastUpdatedDate?: Date) => {
  try {
    let url = `${REACT_APP_TEAMCAMP_APIURL}/v2/project/getProjectList`;

    if (lastUpdatedDate) {
      url = url + `?lastUpdatedTime=${lastUpdatedDate.toISOString()}`;
    }

    const response = await axios.post(url);
    const { data } = response;
    return data as RawProject[];
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get project list please try again';
    captureException(error);
    rootStore.alertNotificationStore.setErrorMessage(error);
    return undefined;
  }
};

/**
 * @desc Project - get project time log
 * @param {*}
 */
export const getProjectTimeLog = async (projectId: string, fromDate: string, toDate: string) => {
  try {
    rootStore.projectStore.setLoading(true);
    const params = {
      fromDate,
      toDate
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/project/${projectId}/timelog`, { params });
    const { data } = response;
    if (data) {
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Project time log Data please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

/**
 * @desc Project - archiveData
 * @param {string} project_id - The ID of the project
 */
export const updateProjectFilter = async (project_id: string, payload: Partial<NewTaskFilterInterface>) => {
  try {
    rootStore.projectStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/project/${project_id}/updateTaskFilter`, payload);
    const { data } = response;
    if (data) {
      const currentProject = rootStore.projectStore.projects.find((project) => project?.id === project_id);
      const updatedFilter = { ...(currentProject?.taskFilterType || {}), ...(payload || {}) };
      const newFilter = mergeTaskFilter(undefined, updatedFilter);
      currentProject?.setTaskFilterType(newFilter);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update project data please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

export const getProjectWiseActivity = async (projectId: string | undefined, page: number, limit: number) => {
  try {
    rootStore.projectStore.setLoading(true);
    if (!projectId) return false;
    const response = await axios.get(
      `${REACT_APP_TEAMCAMP_APIURL}/activity/getProjectWiseActivityList/${projectId}?page=${page}&limit=${limit}`
    );
    const { data } = response;
    if (data) {
      const currentProject = rootStore.projectStore.projects.find((project) => project?.id === projectId);
      if (page && page > 1) {
        currentProject?.appendActivityLogs(data);
      } else {
        currentProject?.setActivityLogs(data);
      }
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get project activity data please try again';
    captureException(error);
    dispatchProjectError(error);
    return false;
  } finally {
    rootStore.projectStore.setLoading(false);
  }
};

export function dispatchProjectError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

export function dispatchProjectSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
